// ─────────────────────────────────────────────────────────────────────────────
// RF-040 — Conversión de las fechas del freeze entre el `<input type="date">` y el contrato.
//
// Las dos direcciones tienen que razonar en el MISMO huso, y ese huso es UTC. Antes no lo hacían:
// la lectura tomaba la fecha UTC del instante (`slice(0, 10)`) mientras la escritura interpretaba
// lo tecleado como medianoche LOCAL (`new Date('2026-08-10T00:00:00')`). En cualquier huso de
// offset positivo el viaje de ida y vuelta perdía un día: teclear "10 de agosto" persistía
// `2026-08-09T22:00:00Z`, el formulario releía "09 de agosto" —no la fecha introducida— y cada
// guardado posterior retrocedía la ventana un día más, aunque el administrador no tocara las
// fechas.
//
// El desplazamiento no es cosmético: con periodicidad semanal mueve un freeze de lunes al domingo
// anterior, es decir, a la SEMANA equivocada. La parada de planta que se quería proteger queda sin
// proteger y se congela una semana que no correspondía — justo lo que `StreakFreezePolicy` dice
// querer evitar ("dejar al administrador convencido de haber protegido una parada de planta").
//
// En México (UTC−6) el defecto no se manifiesta: el viaje de ida y vuelta es estable y el
// desplazamiento de 6 h lo absorbe el predicado de solapamiento del backend. Por eso sobrevivió a
// los gates de S1…S4 y solo aparece en las OU europeas.
// ─────────────────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` exacto: es lo único que emite un `<input type="date">` con valor. */
const DATE_INPUT = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Instante del contrato → valor de un `<input type="date">`: la fecha civil **en UTC**.
 *
 * Tolera que le llegue ya una fecha suelta (`2026-08-10`), porque los documentos anteriores a esta
 * corrección pueden traer la ventana desplazada y hay que poder mostrarlos.
 */
export const isoToDateInput = (iso?: string | null): string => (iso ? iso.slice(0, 10) : '');

/**
 * Valor de un `<input type="date">` → instante del contrato, **siempre** medianoche UTC.
 *
 * El `Z` explícito es lo que vuelve idempotente `isoToDateInput(dateInputToIso(d))` en cualquier
 * huso; sin él el resultado dependía de dónde estuviera el navegador del administrador.
 *
 * Devuelve `null` con el campo vacío (el freeze exige las dos fechas o ninguna, ver
 * `StreakFreezePolicy.Validate`) y también ante un valor con forma inesperada: es preferible que el
 * backend rechace una ventana ausente a persistir una cadena que nadie sabe interpretar.
 */
export const dateInputToIso = (date: string): string | null =>
  DATE_INPUT.test(date) ? `${date}T00:00:00.000Z` : null;
